import type { ParcelFormValues } from '../hooks/useParcelFormSchema';

/**
 * Nome de um campo do formulário de parcela.
 *
 * Derivado das chaves do shape do formulário, e não escrito à mão: renomear um
 * campo do schema quebra em tempo de compilação todas as listas que o citam.
 */
export type ParcelFieldName = keyof ParcelFormValues;

/**
 * Campos que `IndividualPaymentParcelViewModel` — a linha da grid — **não**
 * consegue semear.
 *
 * A grid é um ViewModel de tabela: onde o formulário precisa do valor de entrada,
 * ela devolve o valor derivado ou nada.
 *
 * | Campo | O que a grid tem |
 * | --- | --- |
 * | `fine` | `valueWithFine` — valor já com multa aplicada, não o percentual |
 * | `message` | nada |
 * | `retroactiveDiscount` | nada |
 * | `totalDiscount` | nada |
 * | `invoiceIssueDate` | nada |
 * | `fractionOfParcel` | `installmentAmount` — valor da fração, não a quantidade |
 *
 * Os dois campos derivados não servem de semente: converter `valueWithFine` de
 * volta para percentual exige saber se a multa já incidiu, e a tela não sabe.
 *
 * **A tela de edição não usa mais esta lista**: ela semeia de
 * `GET /parcels/{parcelId}`, que cobre o payload inteiro, e por isso
 * `useParcelEditSeed` devolve `unseededFields` vazio. A constante permanece como o
 * registro do que a grid não cobre — é a fonte de qualquer tela que precise semear
 * uma parcela sem chamar o detalhe, e é o que os testes de
 * `buildParcelEditPayload` usam para exercitar a omissão que protege o
 * full-replace.
 */
export const UNSEEDED_FROM_GRID_FIELDS = [
  'fine',
  'message',
  'retroactiveDiscount',
  'totalDiscount',
  'invoiceIssueDate',
  'fractionOfParcel',
] as const satisfies readonly ParcelFieldName[];

/**
 * O campo está na lista de não semeados?
 *
 * @param fields - Lista devolvida por `useParcelEditSeed`.
 * @param field - Campo a verificar.
 * @returns `true` quando o campo deve ser exibido desabilitado.
 */
export function isUnseededField(
  fields: readonly ParcelFieldName[] | undefined,
  field: ParcelFieldName,
): boolean {
  return fields?.includes(field) === true;
}
